import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { Capacitor } from '@capacitor/core';
import { HttpClient } from '@angular/common/http';
import { ModalController } from '@ionic/angular/lazy';
import { ENV } from 'src/app/app.config';
import { ApplicationEnvironment } from 'src/environments/application-environment.enum';
import { environment } from 'src/environments/environment';
import { AppConfigService } from '@services/app-config.service';
import { UpdateErrorModalComponent } from '@app/shared/update-error-modal/update-error.modal';

interface UpdateInfo {
  version: string;
  url: string;
  buildNumber: string;
  // SHA-256 of the zip; the updater refuses to download a bundle without one.
  checksum: string;
}

// Created by Claude Code AI Agent
// Written before set() reloads the app; the next launch reads it to learn whether the bundle stuck or was rolled back.
interface PendingUpdate {
  version: string;
  bundleId: string;
  environment: ApplicationEnvironment;
  showErrors: boolean;
}

interface UpdateOptions {
  // Download even when the version matches, because the target environment's bundle differs.
  environmentChanged?: boolean;
  showErrors?: boolean;
}

// Created by Claude Code AI Agent
export class OtaUpdateError extends Error {
  constructor(readonly step: string, readonly inner: unknown, readonly context: Record<string, unknown>) {
    super(`${step} failed: ${errorMessage(inner)}`);
  }
}

/* Version follows the scheme {Major}.{Minor}.{Patch}.

 OTA updates are applied if the current version and the new version have the same major and minor versions.*/
@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private readonly version: string;
  private readonly pendingUpdateKey = 'SmartMoving.PendingOtaUpdate';
  private environment: ApplicationEnvironment;

  constructor(
      private readonly http: HttpClient,
      private readonly appConfigService: AppConfigService,
      private readonly modalController: ModalController
  ) {
    this.version = ENV.version;
    this.environment = environment.applicationEnvironment;
  }

  private get manifestUrl() {
    return `https://smappupdates.blob.core.windows.net/premovesurveyapp/${this.environment.toLowerCase()}/version3.json`;
  }

  async needsUpdate() {
    const updateInfo = await this.getLatestUpdate();
    return updateInfo.version !== this.version && this.hasSameMajorAndMinorVersion(updateInfo.version);
  }

  async updateNow({ environmentChanged = false, showErrors = false }: UpdateOptions = {}) {
    const updateInfo = await this.getLatestUpdate();

    // Leaving this in to help with debugging.
    console.log('Got update: ', updateInfo);

    if (environmentChanged || (updateInfo.version !== this.version && this.hasSameMajorAndMinorVersion(updateInfo.version))) {
      await this.applyUpdate(updateInfo, showErrors);
    }
  }

  async changeEnvironment(targetEnvironment: ApplicationEnvironment) {
    const previousEnvironment = this.environment;
    this.environment = targetEnvironment;
    try {
      await this.updateNow({ environmentChanged: true, showErrors: true });
    } catch (err) {
      // A failed switch leaves the app on its old bundle, so later update checks must keep using the old environment.
      this.environment = previousEnvironment;
      throw err;
    }
  }

  // Created by Claude Code AI Agent
  // Call once per launch, after notifyAppReady(), so a rolled-back update is recorded and, for a manual update, shown.
  async resolvePendingUpdate() {
    try {
      const stored = localStorage.getItem(this.pendingUpdateKey);
      if (!stored) {
        return;
      }

      const pending = JSON.parse(stored) as PendingUpdate;
      const { bundle } = await CapacitorUpdater.current();
      // Removed only once the outcome is known, so a failure above keeps the record for the next launch.
      localStorage.removeItem(this.pendingUpdateKey);
      if (bundle.id === pending.bundleId) {
        await this.addUpdateLog(`OTA update to ${pending.version} (${pending.environment}) applied at ${new Date().toUTCString()}`);
        return;
      }

      await this.addUpdateLog(`OTA update to ${pending.version} (${pending.environment}) was rolled back to ${bundle.version} at ${new Date().toUTCString()}`);
      if (pending.showErrors) {
        const rollback = new Error(`The updater rolled back to bundle ${bundle.id} (${bundle.version}). `
            + `Bundle ${pending.bundleId} failed to load or did not call notifyAppReady() in time.`);
        await this.showError(new OtaUpdateError('Start new bundle', rollback, { pendingUpdate: pending, runningBundle: bundle }));
      }
    } catch (err) {
      console.error('Failed to resolve the pending OTA update', err);
    }
  }

  // Created by Claude Code AI Agent
  async showError(error: unknown) {
    console.error(error);
    const modal = await this.modalController.create({
      component: UpdateErrorModalComponent,
      componentProps: { report: await this.buildErrorReport(error) },
      backdropDismiss: false,
      cssClass: 'full-screen-modal'
    });
    await modal.present();
  }

  private async getLatestUpdate() {
    const url = `${this.manifestUrl}?cacheBust=${new Date().getTime()}`;
    // Leaving this in to help with debugging.
    console.log('Retrieving update info from: ', url);
    return await this.runStep('Fetch update info', () => firstValueFrom(
        this.http.get<UpdateInfo>(url, { headers: { skipAuth: 'true' } })), {});
  }

  private async applyUpdate(updateInfo: UpdateInfo, showErrors: boolean) {
    const bundle = await this.runStep('Download bundle', () => {
      // The updater refuses a bundle without a checksum anyway, but on Android its error drops the reason.
      if (!updateInfo.checksum) {
        throw new Error(`The ${this.environment} update ${updateInfo.version} has no checksum`);
      }
      return CapacitorUpdater.download({
        version: updateInfo.version,
        url: updateInfo.url,
        checksum: updateInfo.checksum,
      });
    }, { manifest: updateInfo });

    console.log('Got new version: ', JSON.stringify(bundle));

    if (bundle.status !== 'pending') {
      throw new OtaUpdateError('Download bundle', new Error(`Unexpected bundle status '${bundle.status}'`), { manifest: updateInfo, bundle });
    }

    const pending: PendingUpdate = {
      version: bundle.version,
      bundleId: bundle.id,
      environment: this.environment,
      showErrors
    };
    localStorage.setItem(this.pendingUpdateKey, JSON.stringify(pending));
    await this.addUpdateLog(`OTA update to ${bundle.version} (${this.environment}, bundle ${bundle.id}) started at ${new Date().toUTCString()}`);

    try {
      await CapacitorUpdater.set(bundle);
    } catch (err) {
      localStorage.removeItem(this.pendingUpdateKey);
      await this.addUpdateLog(`OTA update to ${bundle.version} (${this.environment}) failed at ${new Date().toUTCString()}: ${errorMessage(err)}`);
      throw new OtaUpdateError('Apply bundle', err, { manifest: updateInfo, bundle });
    }
  }

  private async runStep<T>(step: string, action: () => Promise<T>, context: Record<string, unknown>) {
    try {
      return await action();
    } catch (err) {
      throw new OtaUpdateError(step, err, { environment: this.environment, manifestUrl: this.manifestUrl, ...context });
    }
  }

  // Best effort: a failed log write must never block or fail the update itself.
  private async addUpdateLog(message: string) {
    try {
      await this.appConfigService.addLoginInfo(message);
    } catch (err) {
      console.error('Failed to record OTA update log', err);
    }
  }

  private async buildErrorReport(error: unknown) {
    const otaError = error instanceof OtaUpdateError ? error : null;
    const lines = [
      `Step: ${otaError?.step ?? 'Unknown'}`,
      `Time: ${new Date().toISOString()}`,
      `App version: ${this.version} (build ${ENV.buildNumber})`,
      `Platform: ${Capacitor.getPlatform()}`,
      `Built-in environment: ${environment.applicationEnvironment}`,
      '',
      describeError(otaError ? otaError.inner : error),
    ];
    if (otaError) {
      lines.push('', 'Context:', stringify(otaError.context));
    }
    lines.push('', 'Updater state:', stringify(await this.getUpdaterState()));
    return lines.join('\n');
  }

  private async getUpdaterState() {
    try {
      const [current, list] = await Promise.all([CapacitorUpdater.current(), CapacitorUpdater.list()]);
      return { current, bundles: list.bundles };
    } catch (err) {
      return `Unavailable: ${errorMessage(err)}`;
    }
  }

  private hasSameMajorAndMinorVersion(newVersion: string) {
    const newVersions = newVersion.split('.');
    const currentVersions = this.version.split('.');
    return currentVersions[0] === newVersions[0] && currentVersions[1] === newVersions[1];
  }
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : stringify(error);
}

// Capacitor plugin rejections carry `code` and `data`; HttpErrorResponse is not an Error, so it falls through to JSON.
function describeError(error: unknown) {
  if (!(error instanceof Error)) {
    return `Error: ${stringify(error)}`;
  }
  const { code, data } = error as Error & { code?: string; data?: unknown };
  return [
    `Error: ${error.name}: ${error.message}`,
    code ? `Code: ${code}` : null,
    data ? `Data: ${stringify(data)}` : null,
    `Stack:\n${error.stack ?? '(none)'}`
  ].filter(line => line !== null).join('\n');
}

function stringify(value: unknown) {
  try {
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
